import { useState, useEffect } from 'react';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function WelcomeModal({ isOpen, onClose }: WelcomeModalProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Small delay for entrance animation
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className={`relative w-full max-w-sm bg-[var(--bg-secondary)] border border-[var(--border)] rounded-2xl p-6 shadow-2xl transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content */}
        <div className="text-center">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--accent)]/10 flex items-center justify-center">
            <svg className="w-8 h-8 text-[var(--accent)]" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/>
              <text x="12" y="16" textAnchor="middle" fontSize="9" fontWeight="bold">L</text>
            </svg>
          </div>

          {/* Title */}
          <h2 className="font-body text-xl font-semibold text-[var(--text-primary)] mb-2">
            Welcome to Lynder
          </h2>

          {/* Message */}
          <p className="font-ui text-sm text-[var(--text-secondary)] mb-6">
            You've received a welcome gift to start your journey.
          </p>

          {/* Coin display */}
          <div className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-[var(--accent)]/5 border border-[var(--accent)]/20 mb-6">
            <svg className="w-5 h-5 text-[var(--accent)]" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/>
              <text x="12" y="16" textAnchor="middle" fontSize="8" fontWeight="bold">L</text>
            </svg>
            <span className="font-body text-2xl font-bold text-[var(--accent)]">100</span>
            <span className="font-ui text-sm text-[var(--text-secondary)]">coins</span>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleClose}
            className="w-full py-3 rounded-xl font-ui text-sm font-medium text-[var(--bg-primary)] bg-[var(--accent)] hover:bg-[var(--accent)]/90 active:scale-[0.98] transition-all"
          >
            Start Reading
          </button>
        </div>
      </div>
    </div>
  );
}
